import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { routesAPI } from '@/services/routes'

// Async thunk to fetch routes from API
export const fetchRoutes = createAsyncThunk(
  'routes/fetchRoutes',
  async (params = {}, { rejectWithValue }) => {
    try {
      const data = await routesAPI.getPublicRoutes(params)
      return data.routes
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch routes')
    }
  }
)

// Async thunk to create a new route
export const createRoute = createAsyncThunk(
  'routes/createRoute',
  async (formData, { rejectWithValue, dispatch }) => {
    try {
      const data = await routesAPI.createRoute(formData)
      // Refresh user routes after creation
      dispatch(fetchUserRoutes())
      return data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create route')
    }
  }
)

// Async thunk to fetch current user's routes
export const fetchUserRoutes = createAsyncThunk(
  'routes/fetchUserRoutes',
  async (_, { rejectWithValue }) => {
    try {
      const data = await routesAPI.getUserRoutes()
      return data.routes
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch user routes')
    }
  }
)

// Async thunk to update a route
export const updateRoute = createAsyncThunk(
  'routes/updateRoute',
  async ({ id, payload }, { rejectWithValue, dispatch }) => {
    try {
      const data = await routesAPI.updateRoute(id, payload)
      // Refresh user routes after update
      dispatch(fetchUserRoutes())
      return data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update route')
    }
  }
)

const routesSlice = createSlice({
  name: 'routes',
  initialState: {
    selectedState: '',
    difficulty: '',
    sort: 'recent',
    routes: [],
    userRoutes: [],
    isLoading: false,
    submitLoading: false,
    error: null,
  },
  reducers: {
    setSelectedState: (state, action) => {
      state.selectedState = action.payload
    },
    setDifficulty: (state, action) => {
      state.difficulty = action.payload
    },
    setSort: (state, action) => {
      state.sort = action.payload
    },
    clearError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRoutes.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchRoutes.fulfilled, (state, action) => {
        state.isLoading = false
        state.routes = action.payload
        state.error = null
      })
      .addCase(fetchRoutes.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload
      })
      // Create route
      .addCase(createRoute.pending, (state) => {
        state.submitLoading = true
        state.error = null
      })
      .addCase(createRoute.fulfilled, (state) => {
        state.submitLoading = false
      })
      .addCase(createRoute.rejected, (state, action) => {
        state.submitLoading = false
        state.error = action.payload
      })
      // Fetch user routes
      .addCase(fetchUserRoutes.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchUserRoutes.fulfilled, (state, action) => {
        state.isLoading = false
        state.userRoutes = action.payload
        state.error = null
      })
      .addCase(fetchUserRoutes.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload
      })
      // Update route
      .addCase(updateRoute.pending, (state) => {
        state.submitLoading = true
        state.error = null
      })
      .addCase(updateRoute.fulfilled, (state) => {
        state.submitLoading = false
      })
      .addCase(updateRoute.rejected, (state, action) => {
        state.submitLoading = false
        state.error = action.payload
      })
  },
})

export const { setSelectedState, setDifficulty, setSort, clearError } = routesSlice.actions
export default routesSlice.reducer
